'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

// ─── DUAL VOICE SYSTEM ────────────────────────────────────────────────────────
// System voice: logistics, navigation, data
// Companion voice: journey, vernacular, Open Letter register

export type VoiceRegister = 'system' | 'companion';

interface VoiceMessage {
  system: string;
  companion: string;
  arabic?: string;
  trigger: string;
}

const VOICE_MESSAGES: Record<string, VoiceMessage> = {
  garden_greeting_morning: {
    system: 'Good morning, Caretaker. Your garden is ready.',
    companion: 'Life starts at the checkout, we still in the aisles. What are you putting in your cart today?',
    arabic: 'بِسْمِ اللَّهِ',
    trigger: 'morning',
  },
  garden_greeting_fajr: {
    system: 'Fajr. The garden is at its most receptive.',
    companion: 'The sand nearest the frozen tides. The boundary is thinner than it looks right now.',
    arabic: 'الفَجْر',
    trigger: 'fajr',
  },
  garden_greeting_return: {
    system: 'Welcome back. Your garden has been tended in your absence.',
    companion: 'You were gone a minute. The garden kept growing. That\'s how it works.',
    trigger: 'return',
  },
  first_module_plant: {
    system: 'Module planted. Begin cultivation.',
    companion: 'A structure formed reflecting on sample examinations, commentary, and field notes. You just started yours.',
    arabic: 'بِسْمِ اللَّهِ',
    trigger: 'first_plant',
  },
  observation_submitted: {
    system: 'Observation recorded. Recognition pathway initiated.',
    companion: 'Processing what you see. A bit of how you see it. What you recognize on the way.',
    trigger: 'observation',
  },
  mizan_imbalance_recognition: {
    system: 'Recognition activity exceeds service activity. Consider bridging to MRG.',
    companion: 'You\'ve been seeing a lot. What\'s this recognition calling you to do? Aware of a good to do, encouraged to be conscious of how.',
    trigger: 'mizan_recognition_heavy',
  },
  mizan_imbalance_action: {
    system: 'Service activity exceeds recognition activity. Consider IRL Lens engagement.',
    companion: 'You\'ve been doing. Have you paused to ask the Three Questions of any of it? What is this resource? What Names compose its provision?',
    trigger: 'mizan_action_heavy',
  },
  depth_unlock: {
    system: 'New depth layer accessible.',
    companion: 'You\'ve been walking on water for a while now. You just noticed.',
    trigger: 'depth_unlock',
  },
  amanah_quest_start: {
    system: 'Amanah quest initiated. Record your intention.',
    companion: 'A test to see which of us is excellent in work with the trust that we are granted care over. What\'s this for? Be real about it.',
    arabic: 'أَمَانَة',
    trigger: 'quest_start',
  },
  ward_disparity_high: {
    system: 'Ward disparity detected. Amanah of Ar-Razzaq requires response.',
    companion: 'Somebody\'s hungry in your neighborhood. What you gonna do about it?',
    arabic: 'الرَّزَّاق',
    trigger: 'ward_disparity',
  },
  module_dormant: {
    system: 'Module entering dormancy. Last engagement: {days} days ago.',
    companion: 'As-Sabur — The Patient. The root system is alive beneath the frost. Come back when you\'re ready.',
    arabic: 'الصَّبُور',
    trigger: 'dormant',
  },
  harvest_cycle: {
    system: 'Recognition cycle complete. Begin next cycle.',
    companion: 'You harvested. Now the soil is richer. What are you planting next? We still in the aisles.',
    arabic: 'الحَمْدُ لِلَّه',
    trigger: 'harvest',
  },
  garden_exit: {
    system: 'Session ending. Journey archived.',
    companion: 'You\'ve been entrusted with what you\'ve seen. Go tend it.',
    arabic: 'أَمَانَة',
    trigger: 'exit',
  },
  loading_default: {
    system: 'Loading your garden...',
    companion: 'Form follows function on a journey to walk on some water.',
    trigger: 'loading',
  },
  loading_fajr: {
    system: 'Fajr garden loading...',
    companion: 'I didn\'t immediately notice when I transitioned from that snow covered sand onto the water.',
    trigger: 'loading_fajr',
  },
  empty_garden: {
    system: 'No modules planted. Begin with the nearest.',
    companion: 'Your ward needs a caretaker. Begin with what you can see.',
    trigger: 'empty',
  },
  community_gate: {
    system: 'Observation quality threshold not met for community sharing.',
    companion: 'Submit first. Then share. Those who submit to revelation call to it.',
    trigger: 'community_gate',
  },
};

// ─── DUAL VOICE DISPLAY ───────────────────────────────────────────────────────

interface DualVoiceDisplayProps {
  messageKey: string;
  defaultRegister?: VoiceRegister;
  compact?: boolean;
  onRegisterChange?: (register: VoiceRegister) => void;
}

export function DualVoiceDisplay({
  messageKey,
  defaultRegister = 'companion',
  compact = false,
  onRegisterChange,
}: DualVoiceDisplayProps) {
  const [register, setRegister] = useState<VoiceRegister>(defaultRegister);
  const message = VOICE_MESSAGES[messageKey];

  if (!message) return null;

  const handleToggle = (r: VoiceRegister) => {
    setRegister(r);
    onRegisterChange?.(r);
  };

  const currentText = register === 'system' ? message.system : message.companion;

  if (compact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <p style={{
          fontSize: 11, color: register === 'companion' ? '#d4c5a9' : 'rgba(212,197,169,0.6)',
          fontStyle: register === 'companion' ? 'italic' : 'normal',
          fontFamily: register === 'companion' ? "'Georgia', serif" : 'monospace',
          lineHeight: 1.5, flex: 1,
        }}>
          {currentText}
        </p>
        <button
          onClick={() => handleToggle(register === 'system' ? 'companion' : 'system')}
          title={`Switch to ${register === 'system' ? 'companion' : 'system'} voice`}
          style={{
            background: 'none', border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 4, padding: '3px 7px',
            fontSize: 8, color: 'rgba(201,168,76,0.5)',
            cursor: 'pointer', fontFamily: 'monospace', letterSpacing: '.08em',
            flexShrink: 0,
          }}
        >
          {register === 'system' ? '◈' : '⌇'}
        </button>
      </div>
    );
  }

  return (
    <div style={{
      background: 'rgba(6,13,18,0.5)',
      border: '1px solid rgba(201,168,76,0.12)',
      borderRadius: 8,
      overflow: 'hidden',
    }}>
      {/* Voice toggle tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid rgba(201,168,76,0.1)',
      }}>
        {(['companion', 'system'] as VoiceRegister[]).map(r => (
          <button
            key={r}
            onClick={() => handleToggle(r)}
            style={{
              flex: 1,
              padding: '8px 12px',
              background: register === r ? 'rgba(201,168,76,0.06)' : 'transparent',
              border: 'none',
              borderBottom: register === r ? '2px solid rgba(201,168,76,0.5)' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase',
              color: register === r ? '#c9a84c' : 'rgba(212,197,169,0.35)',
              fontFamily: 'monospace',
              transition: 'all 0.2s',
            }}
          >
            {r === 'companion' ? '◈ Companion Voice' : '⌇ System Voice'}
          </button>
        ))}
      </div>

      {/* Message */}
      <div style={{ padding: '14px 16px' }}>
        {message.arabic && register === 'companion' && (
          <div style={{
            fontFamily: "'Amiri', serif",
            fontSize: 16, color: 'rgba(201,168,76,0.5)',
            direction: 'rtl', marginBottom: 8, textAlign: 'right',
          }}>
            {message.arabic}
          </div>
        )}
        <p style={{
          fontSize: 12,
          color: register === 'companion' ? '#d4c5a9' : 'rgba(212,197,169,0.6)',
          fontStyle: register === 'companion' ? 'italic' : 'normal',
          fontFamily: register === 'companion' ? "'Georgia', serif" : 'monospace',
          lineHeight: 1.7,
          transition: 'all 0.2s',
        }}>
          {currentText}
        </p>
      </div>
    </div>
  );
}

// ─── GARDEN LOADING SCREEN WITH VERNACULAR ────────────────────────────────────

export function GardenLoadingVoice({ prayerState }: { prayerState?: string }) {
  const [phase, setPhase] = useState(0);
  const messages = [
    { text: 'Form follows function on a journey to walk on some water.', arabic: '' },
    { text: 'The curriculum begins with the sense(s) and builds toward environmental responsibility.', arabic: 'الحَوَاس' },
    { text: 'Soil and nutrients, foundational elements that support growth.', arabic: 'التُّرْبَة' },
    { text: 'Life starts at the checkout, we still in the aisles.', arabic: '' },
    { text: 'I didn\'t immediately notice when I transitioned from that snow covered sand onto the water.', arabic: 'بَرْزَخ' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setPhase(p => (p + 1) % messages.length);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  const current = messages[phase];

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '32px 24px', gap: 12,
    }}>
      {/* Pulse ring */}
      <div style={{
        width: 40, height: 40, borderRadius: '50%',
        border: '1px solid rgba(201,168,76,0.3)',
        animation: 'gardenLoadPulse 2s ease-in-out infinite',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: 'rgba(201,168,76,0.5)',
          animation: 'gardenLoadPulse 2s ease-in-out infinite 0.5s',
        }} />
      </div>

      {current.arabic && (
        <div style={{
          fontFamily: "'Amiri', serif",
          fontSize: 18, color: 'rgba(201,168,76,0.4)',
          direction: 'rtl',
          transition: 'opacity 0.5s ease',
        }}>
          {current.arabic}
        </div>
      )}

      <p style={{
        fontFamily: "'Georgia', serif",
        fontStyle: 'italic',
        fontSize: 13, color: 'rgba(212,197,169,0.6)',
        textAlign: 'center', maxWidth: 320, lineHeight: 1.7,
        transition: 'opacity 0.5s ease',
      }}>
        "{current.text}"
      </p>

      <style>{`
        @keyframes gardenLoadPulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 0.2; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
}

// ─── BARZAKH TRANSITION SYSTEM ────────────────────────────────────────────────
// Gradual discovery — no announcements. The caretaker realizes after they've crossed.

interface BarzakhTransitionProps {
  type: 'depth_unlock' | 'biome_crossing' | 'recognition_milestone' | 'stage_transition';
  fromLabel: string;
  toLabel: string;
  onReveal?: () => void;
}

export function BarzakhTransition({ type, fromLabel, toLabel, onReveal }: BarzakhTransitionProps) {
  const [phase, setPhase] = useState<'hidden' | 'dissolving' | 'revealed'>('hidden');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Delay the reveal — the caretaker has already crossed before they notice
    const t1 = setTimeout(() => setPhase('dissolving'), 1200);
    const t2 = setTimeout(() => {
      setPhase('revealed');
      onReveal?.();
    }, 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  if (dismissed || phase === 'hidden') return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 80, left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 500,
        opacity: phase === 'dissolving' ? 0.4 : phase === 'revealed' ? 1 : 0,
        transition: 'opacity 1.8s ease',
        pointerEvents: phase === 'revealed' ? 'all' : 'none',
        maxWidth: 360, width: 'calc(100vw - 48px)',
      }}
    >
      <div style={{
        background: 'rgba(6,13,18,0.95)',
        border: '1px solid rgba(201,168,76,0.2)',
        borderRadius: 10,
        padding: '16px 20px',
        backdropFilter: 'blur(12px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8, fontFamily: 'monospace' }}>
              بَرْزَخ — You've been here a while
            </div>
            <p style={{ fontSize: 12, color: '#d4c5a9', lineHeight: 1.6, fontStyle: 'italic', fontFamily: "'Georgia', serif", marginBottom: 8 }}>
              You crossed from <span style={{ color: 'rgba(201,168,76,0.7)' }}>{fromLabel}</span> into <span style={{ color: '#c9a84c' }}>{toLabel}</span> without noticing. The ground changed beneath you.
            </p>
            <p style={{ fontSize: 10, color: '#7a6f5d', lineHeight: 1.5, fontFamily: 'monospace' }}>
              "I didn't immediately notice when I transitioned from that snow covered sand onto the water."
            </p>
          </div>
          <button
            onClick={() => setDismissed(true)}
            style={{
              background: 'none', border: 'none',
              color: 'rgba(201,168,76,0.4)', cursor: 'pointer',
              fontSize: 16, flexShrink: 0, padding: 4,
            }}
          >×</button>
        </div>
      </div>
    </div>
  );
}

// ─── FIKR JOURNAL ─────────────────────────────────────────────────────────────
// Free-prose observation mode — no prompts, no frameworks, just writing

interface FikrEntry {
  id: string;
  text: string;
  timestamp: string;
  mood?: string;
}

export function FikrJournal() {
  const [entries, setEntries] = useState<FikrEntry[]>([]);
  const [currentText, setCurrentText] = useState('');
  const [isWriting, setIsWriting] = useState(false);
  const [viewMode, setViewMode] = useState<'write' | 'read'>('write');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('haal_fikr_entries');
      if (saved) {
        try { setEntries(JSON.parse(saved)); } catch {}
      }
    }
  }, []);

  const saveEntry = () => {
    if (!currentText.trim()) return;
    const entry: FikrEntry = {
      id: Date.now().toString(),
      text: currentText.trim(),
      timestamp: new Date().toISOString(),
    };
    const updated = [entry, ...entries];
    setEntries(updated);
    if (typeof window !== 'undefined') {
      localStorage.setItem('haal_fikr_entries', JSON.stringify(updated.slice(0, 50)));
    }
    setCurrentText('');
    setIsWriting(false);
    setViewMode('read');
  };

  const deleteEntry = (id: string) => {
    const updated = entries.filter(e => e.id !== id);
    setEntries(updated);
    if (typeof window !== 'undefined') {
      localStorage.setItem('haal_fikr_entries', JSON.stringify(updated));
    }
  };

  return (
    <div style={{
      background: 'rgba(6,13,18,0.6)',
      border: '1px solid rgba(201,168,76,0.15)',
      borderRadius: 10,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid rgba(201,168,76,0.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: '#c9a84c', direction: 'rtl' }}>فِكْر</span>
            <span style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', fontFamily: 'monospace' }}>FIKR JOURNAL</span>
          </div>
          <p style={{ fontSize: 10, color: '#7a6f5d', fontFamily: 'monospace' }}>
            Free-prose field notes. No prompts. No frameworks. Just writing.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {(['write', 'read'] as const).map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              style={{
                fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                padding: '5px 10px', borderRadius: 4,
                background: viewMode === mode ? 'rgba(201,168,76,0.1)' : 'transparent',
                border: `1px solid ${viewMode === mode ? 'rgba(201,168,76,0.35)' : 'rgba(201,168,76,0.12)'}`,
                color: viewMode === mode ? '#c9a84c' : 'rgba(212,197,169,0.35)',
                cursor: 'pointer',
              }}
            >
              {mode === 'write' ? '✦ Write' : `◎ Notes (${entries.length})`}
            </button>
          ))}
        </div>
      </div>

      {/* Write mode */}
      {viewMode === 'write' && (
        <div style={{ padding: '20px' }}>
          {!isWriting ? (
            <button
              onClick={() => { setIsWriting(true); setTimeout(() => textareaRef.current?.focus(), 50); }}
              style={{
                width: '100%',
                background: 'rgba(201,168,76,0.03)',
                border: '1px dashed rgba(201,168,76,0.2)',
                borderRadius: 8,
                padding: '20px',
                cursor: 'pointer',
                textAlign: 'left',
                color: 'rgba(212,197,169,0.35)',
                fontSize: 13,
                fontStyle: 'italic',
                fontFamily: "'Georgia', serif",
                lineHeight: 1.6,
                transition: 'all 0.2s',
              }}
            >
              "I noticed the sand doing it too..."
              <br />
              <span style={{ fontSize: 10, fontFamily: 'monospace', fontStyle: 'normal', letterSpacing: '.08em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.3)', marginTop: 8, display: 'block' }}>
                Tap to write a field note
              </span>
            </button>
          ) : (
            <div>
              <textarea
                ref={textareaRef}
                value={currentText}
                onChange={e => setCurrentText(e.target.value)}
                placeholder="Write what you saw and what it meant, in your own voice, in your own register, without prompts or frameworks..."
                style={{
                  width: '100%',
                  minHeight: 160,
                  background: 'rgba(6,13,18,0.8)',
                  border: '1px solid rgba(201,168,76,0.2)',
                  borderRadius: 8,
                  padding: '16px',
                  color: '#d4c5a9',
                  fontSize: 13,
                  fontFamily: "'Georgia', serif",
                  fontStyle: 'italic',
                  lineHeight: 1.8,
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setIsWriting(false); setCurrentText(''); }}
                  style={{
                    fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                    padding: '7px 14px', borderRadius: 4,
                    background: 'transparent', border: '1px solid rgba(255,255,255,0.08)',
                    color: 'rgba(212,197,169,0.4)', cursor: 'pointer',
                  }}
                >
                  Discard
                </button>
                <button
                  onClick={saveEntry}
                  disabled={!currentText.trim()}
                  style={{
                    fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                    padding: '7px 14px', borderRadius: 4,
                    background: currentText.trim() ? 'rgba(201,168,76,0.12)' : 'rgba(201,168,76,0.04)',
                    border: `1px solid ${currentText.trim() ? 'rgba(201,168,76,0.4)' : 'rgba(201,168,76,0.1)'}`,
                    color: currentText.trim() ? '#c9a84c' : 'rgba(201,168,76,0.3)',
                    cursor: currentText.trim() ? 'pointer' : 'not-allowed',
                  }}
                >
                  Save field note
                </button>
              </div>
              <p style={{ fontSize: 9, color: 'rgba(212,197,169,0.25)', fontFamily: 'monospace', marginTop: 8 }}>
                Field notes don't earn Alhamdulilah milestones. They don't advance the depth system. They're just writing. The garden holds this alongside the structured observations — equal space, equal dignity.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Read mode */}
      {viewMode === 'read' && (
        <div style={{ padding: '16px 20px' }}>
          {entries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.35)', fontStyle: 'italic', fontFamily: "'Georgia', serif", lineHeight: 1.7 }}>
                "I noticed the sand doing it too."<br />
                <span style={{ fontSize: 10, fontFamily: 'monospace', fontStyle: 'normal', color: 'rgba(212,197,169,0.2)' }}>
                  Your field notes will appear here.
                </span>
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {entries.map(entry => (
                <div
                  key={entry.id}
                  style={{
                    background: 'rgba(201,168,76,0.03)',
                    border: '1px solid rgba(201,168,76,0.1)',
                    borderLeft: '2px solid rgba(201,168,76,0.25)',
                    borderRadius: '0 6px 6px 0',
                    padding: '14px 16px',
                  }}
                >
                  <p style={{
                    fontSize: 13, color: '#d4c5a9',
                    fontStyle: 'italic', fontFamily: "'Georgia', serif",
                    lineHeight: 1.8, marginBottom: 8,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {entry.text}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 9, color: 'rgba(212,197,169,0.3)', fontFamily: 'monospace' }}>
                      {new Date(entry.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <button
                      onClick={() => deleteEntry(entry.id)}
                      style={{
                        background: 'none', border: 'none',
                        color: 'rgba(212,197,169,0.2)', cursor: 'pointer',
                        fontSize: 10, fontFamily: 'monospace',
                        padding: '2px 6px',
                      }}
                    >
                      remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── GARMENT-GARDEN FEEDBACK LOOP ─────────────────────────────────────────────
// Thread colors influence garden module responsiveness

export type GarmentThreadType = 'recognition' | 'service' | 'irl' | 'mrg' | 'keys' | 'community';

interface GarmentThread {
  type: GarmentThreadType;
  color: string;
  label: string;
  arabic: string;
  count: number;
  modulesEnhanced: string[];
  description: string;
}

const THREAD_DEFINITIONS: Record<GarmentThreadType, Omit<GarmentThread, 'count'>> = {
  recognition: {
    type: 'recognition',
    color: '#2d8a87',
    label: 'Recognition',
    arabic: 'مَعْرِفَة',
    modulesEnhanced: ['IRL Lens', 'Names Reference', 'Ayat in Creation'],
    description: 'Recognition threads deepen IRL Lens responsiveness — the lens sees more clearly when the garment carries recognition.',
  },
  service: {
    type: 'service',
    color: '#2d7a47',
    label: 'Service',
    arabic: 'خِدْمَة',
    modulesEnhanced: ['Muslim Resource Guide', 'Community', 'Quests'],
    description: 'Service threads enrich MRG caretaking modules — the ward data becomes more specific when the garment carries service.',
  },
  irl: {
    type: 'irl',
    color: '#2d8a87',
    label: 'IRL Lens',
    arabic: 'عَدَسَة',
    modulesEnhanced: ['IRL Lens', 'Observation Chain', 'Fission Sequences'],
    description: 'IRL threads make the lens more responsive — each observation adds a thread that sharpens the next encounter.',
  },
  mrg: {
    type: 'mrg',
    color: '#5a8a5a',
    label: 'MRG',
    arabic: 'الْمَوَارِد',
    modulesEnhanced: ['Muslim Resource Guide', 'Ward Data', 'Amanah Quests'],
    description: 'MRG threads enrich caretaking modules — service woven into the garment makes the ward\'s needs more visible.',
  },
  keys: {
    type: 'keys',
    color: '#D4A82A',
    label: 'Keys',
    arabic: 'مَفَاتِيح',
    modulesEnhanced: ['Keys to Kulli Haal', 'SL Glossary', 'Names Reference'],
    description: 'Keys threads deepen the curriculum — each key engaged adds a thread that illuminates the next key.',
  },
  community: {
    type: 'community',
    color: '#8a8ab8',
    label: 'Community',
    arabic: 'جَمَاعَة',
    modulesEnhanced: ['Community', 'Fisabilillah Feed', 'Cohorts'],
    description: 'Community threads connect the garden to the collective — the garment carries the resonance of other caretakers.',
  },
};

interface GarmentGardenFeedbackProps {
  resonanceHistory: { resonance_type: string }[];
  questsCompleted: number;
}

export function GarmentGardenFeedback({ resonanceHistory, questsCompleted }: GarmentGardenFeedbackProps) {
  const [expanded, setExpanded] = useState(false);

  // Compute thread counts from history
  const threadCounts: Record<GarmentThreadType, number> = {
    recognition: resonanceHistory.filter(h => ['verse_click', 'deep_read', 'term_expand'].includes(h.resonance_type)).length,
    service: questsCompleted * 3,
    irl: resonanceHistory.filter(h => h.resonance_type === 'module_visit').length,
    mrg: resonanceHistory.filter(h => h.resonance_type === 'module_visit').length,
    keys: resonanceHistory.filter(h => ['glossary_search', 'glossary_term'].includes(h.resonance_type)).length,
    community: resonanceHistory.filter(h => h.resonance_type === 'view').length,
  };

  const threads: GarmentThread[] = Object.entries(THREAD_DEFINITIONS).map(([type, def]) => ({
    ...def,
    count: threadCounts[type as GarmentThreadType],
  }));

  const dominantThread = threads.reduce((a, b) => a.count > b.count ? a : b);
  const totalThreads = threads.reduce((sum, t) => sum + t.count, 0);

  return (
    <div style={{
      background: 'rgba(6,13,18,0.5)',
      border: '1px solid rgba(138,138,184,0.2)',
      borderRadius: 10,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '14px 18px',
          display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: '#8a8ab8', direction: 'rtl' }}>رِشَاط</span>
            <span style={{ fontSize: 8, color: '#8a8ab8', letterSpacing: '.15em', textTransform: 'uppercase', fontFamily: 'monospace' }}>GARMENT → GARDEN FEEDBACK</span>
          </div>
          <p style={{ fontSize: 11, color: '#7a6f5d', lineHeight: 1.5 }}>
            {totalThreads > 0
              ? `${totalThreads} threads woven. Dominant: ${dominantThread.label} — enhancing ${dominantThread.modulesEnhanced[0]}.`
              : 'No threads yet. What you cultivate becomes what you wear.'}
          </p>
        </div>
        {/* Thread color preview */}
        <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
          {threads.filter(t => t.count > 0).slice(0, 4).map(t => (
            <div
              key={t.type}
              style={{
                width: 6, height: 24, borderRadius: 3,
                background: t.color,
                opacity: Math.min(1, 0.3 + (t.count / Math.max(1, totalThreads)) * 0.7),
              }}
            />
          ))}
        </div>
        <div style={{ color: '#8a8ab8', fontSize: 14, transform: expanded ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>+</div>
      </button>

      {/* Expanded */}
      {expanded && (
        <div style={{ padding: '0 18px 18px', borderTop: '1px solid rgba(138,138,184,0.1)' }}>
          <p style={{ fontSize: 11, color: '#7a6f5d', lineHeight: 1.6, margin: '14px 0 16px', fontStyle: 'italic' }}>
            "These bodies are the garments we wear, the seeds we plant." — You wear what you grow. You grow what you wear.
          </p>

          {/* Thread grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
            {threads.map(thread => (
              <div
                key={thread.type}
                style={{
                  background: `${thread.color}08`,
                  border: `1px solid ${thread.color}${thread.count > 0 ? '30' : '12'}`,
                  borderTop: `2px solid ${thread.color}${thread.count > 0 ? '60' : '20'}`,
                  borderRadius: 6,
                  padding: '10px 12px',
                  opacity: thread.count > 0 ? 1 : 0.5,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: thread.color, direction: 'rtl' }}>
                    {thread.arabic}
                  </span>
                  <span style={{ fontSize: 10, color: thread.color, fontFamily: 'monospace', fontWeight: 700 }}>
                    {thread.count}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: '#d4c5a9', fontWeight: 600, marginBottom: 4 }}>{thread.label}</div>
                <div style={{ fontSize: 9, color: '#7a6f5d', lineHeight: 1.4 }}>
                  Enhances: {thread.modulesEnhanced[0]}
                </div>
                {/* Thread bar */}
                <div style={{ height: 2, background: 'rgba(255,255,255,0.06)', borderRadius: 1, marginTop: 8 }}>
                  <div style={{
                    height: '100%', borderRadius: 1,
                    background: thread.color,
                    width: `${Math.min(100, (thread.count / Math.max(1, totalThreads)) * 100)}%`,
                    transition: 'width 0.5s ease',
                  }} />
                </div>
              </div>
            ))}
          </div>

          {dominantThread.count > 0 && (
            <div style={{
              marginTop: 14,
              background: `${dominantThread.color}08`,
              border: `1px solid ${dominantThread.color}20`,
              borderRadius: 6, padding: '12px 14px',
            }}>
              <div style={{ fontSize: 8, color: dominantThread.color, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 6, fontFamily: 'monospace' }}>
                ◈ DOMINANT THREAD EFFECT
              </div>
              <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.7)', lineHeight: 1.6 }}>
                {dominantThread.description}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MURAL NEAREST-FIRST DISPLAY ──────────────────────────────────────────────
// Caretaker's city/ward foregrounded, rest of spiral dimmed until nearest is recognized

interface BiomeRegion {
  id: string;
  label: string;
  arabic: string;
  description: string;
  color: string;
  ring: number; // 1 = nearest, 7 = furthest
  sequences: string[];
  hikmahNote: string;
}

const BIOME_REGIONS: BiomeRegion[] = [
  {
    id: 'forest',
    label: 'Forest Watershed',
    arabic: 'الغَابَة',
    description: 'Multi-layered forest from highlands to valleys, canopy to roots. The nearest path.',
    color: '#2d7a47',
    ring: 1,
    sequences: ['Canopy Photosynthesis', 'Mycorrhizal Network', 'Longleaf Pine Grass Stage'],
    hikmahNote: 'Al-Muhyi — The Life-Giver. Every layer of the forest is a different expression of the same Name.',
  },
  {
    id: 'wetland',
    label: 'Wetland Filtration',
    arabic: 'الأَهْوَار',
    description: 'Where water is purified through ecological process. Al-Quddus made visible.',
    color: '#2d8a87',
    ring: 2,
    sequences: ['Wetland Filtration', 'Amphibian Metamorphosis'],
    hikmahNote: 'Al-Quddus — The Pure. The wetland purifies what passes through it. What passes through you?',
  },
  {
    id: 'grassland',
    label: 'Tallgrass Prairie',
    arabic: 'السَّهْل',
    description: 'Big Bluestem and the underground root system. As-Sabur in the dormant season.',
    color: '#8a7a2a',
    ring: 3,
    sequences: ['Big Bluestem', 'Prairie Fire Cycle'],
    hikmahNote: 'As-Sabur — The Patient. Five years looking like failure while building a 500-year foundation.',
  },
  {
    id: 'river',
    label: 'River Systems',
    arabic: 'الأَنْهَار',
    description: 'Salmon Run and the return cycle. Al-Hayy — the Living — in the water.',
    color: '#2a5a8a',
    ring: 4,
    sequences: ['Salmon Run', 'River Delta Formation'],
    hikmahNote: 'Al-Hayy — The Living. The salmon returns to where it began. What is your return?',
  },
  {
    id: 'coastal',
    label: 'Coastal Ecotone',
    arabic: 'السَّاحِل',
    description: 'The edge where land meets water. The Barzakh — the dissolved boundary.',
    color: '#4a6a8a',
    ring: 5,
    sequences: ['Tidal Zone', 'Coastal Erosion'],
    hikmahNote: 'Al-Barzakh — The Barrier. "I didn\'t immediately notice when I transitioned from that snow covered sand onto the water."',
  },
  {
    id: 'desert',
    label: 'Desert Ecology',
    arabic: 'الصَّحْرَاء',
    description: 'Where scarcity reveals provision. Ar-Razzaq in the most austere landscape.',
    color: '#8a5a2a',
    ring: 6,
    sequences: ['Desert Bloom', 'Cactus Water Storage'],
    hikmahNote: 'Ar-Razzaq — The Provider. Even in the desert, provision is present. Where is it in your ward?',
  },
  {
    id: 'quantum',
    label: 'Quantum Threshold',
    arabic: 'العَتَبَة',
    description: 'The boundary of the visible. Where the Zahir dissolves into the Batin.',
    color: '#6a5a8a',
    ring: 7,
    sequences: ['Bioluminescence', 'Mycelium Network'],
    hikmahNote: 'Al-Batin — The Hidden. The threshold where what you can see gives way to what you can only recognize.',
  },
];

interface MuralNearestFirstProps {
  userCity?: string;
  recognizedBiomes?: string[];
}

export function MuralNearestFirst({ userCity = 'chicago', recognizedBiomes = [] }: MuralNearestFirstProps) {
  const [hoveredBiome, setHoveredBiome] = useState<string | null>(null);
  const [selectedBiome, setSelectedBiome] = useState<string | null>(null);

  // Nearest biome based on city
  const cityBiomeMap: Record<string, string> = {
    chicago: 'grassland',
    atlanta: 'forest',
    portland: 'forest',
    detroit: 'river',
    durham: 'forest',
    louisville: 'river',
    cincinnati: 'river',
    pittsburgh: 'forest',
  };

  const nearestBiomeId = cityBiomeMap[userCity] || 'forest';
  const nearestBiome = BIOME_REGIONS.find(b => b.id === nearestBiomeId)!;

  const isRecognized = (biomeId: string) =>
    recognizedBiomes.includes(biomeId) || biomeId === nearestBiomeId;

  const getDimness = (biome: BiomeRegion) => {
    if (biome.id === nearestBiomeId) return 1;
    if (isRecognized(biome.id)) return 0.85;
    // Dim based on ring distance from nearest
    const nearestRing = nearestBiome.ring;
    const distance = Math.abs(biome.ring - nearestRing);
    return Math.max(0.15, 1 - distance * 0.15);
  };

  const selected = selectedBiome ? BIOME_REGIONS.find(b => b.id === selectedBiome) : null;

  return (
    <div style={{
      background: 'rgba(6,13,18,0.7)',
      border: '1px solid rgba(201,168,76,0.15)',
      borderRadius: 12,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid rgba(201,168,76,0.1)',
        background: 'rgba(201,168,76,0.03)',
      }}>
        <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.18em', textTransform: 'uppercase', marginBottom: 6, fontFamily: 'monospace' }}>
          الجِدَارِيَّة — THE MURAL
        </div>
        <p style={{ fontSize: 12, color: '#7a6f5d', lineHeight: 1.5 }}>
          "A path through the nearest emerges." Your city: <span style={{ color: '#c9a84c' }}>{userCity.charAt(0).toUpperCase() + userCity.slice(1)}</span>. Nearest biome: <span style={{ color: nearestBiome.color }}>{nearestBiome.label}</span>.
        </p>
      </div>

      {/* Spiral rings — nearest foregrounded */}
      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {BIOME_REGIONS.sort((a, b) => {
          // Sort: nearest first, then by ring
          if (a.id === nearestBiomeId) return -1;
          if (b.id === nearestBiomeId) return 1;
          return a.ring - b.ring;
        }).map(biome => {
          const dimness = getDimness(biome);
          const isNearest = biome.id === nearestBiomeId;
          const isHovered = hoveredBiome === biome.id;
          const isSelected = selectedBiome === biome.id;

          return (
            <button
              key={biome.id}
              onClick={() => setSelectedBiome(isSelected ? null : biome.id)}
              onMouseEnter={() => setHoveredBiome(biome.id)}
              onMouseLeave={() => setHoveredBiome(null)}
              style={{
                width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                textAlign: 'left',
                opacity: dimness,
                transition: 'opacity 0.3s ease',
              }}
            >
              <div style={{
                background: isNearest ? `${biome.color}12` : `${biome.color}06`,
                border: `1px solid ${biome.color}${isNearest ? '40' : '20'}`,
                borderLeft: `3px solid ${biome.color}${isNearest ? '80' : '30'}`,
                borderRadius: '0 8px 8px 0',
                padding: '12px 16px',
                transition: 'all 0.2s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {/* Ring indicator */}
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%',
                    border: `1px solid ${biome.color}40`,
                    background: isNearest ? `${biome.color}20` : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 8, color: biome.color, fontFamily: 'monospace',
                    flexShrink: 0,
                  }}>
                    {biome.ring}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: biome.color, direction: 'rtl' }}>
                        {biome.arabic}
                      </span>
                      <span style={{ fontSize: 11, color: '#d4c5a9', fontWeight: 600 }}>
                        {biome.label}
                      </span>
                      {isNearest && (
                        <span style={{
                          fontSize: 7, color: biome.color, letterSpacing: '.12em', textTransform: 'uppercase',
                          background: `${biome.color}15`, border: `1px solid ${biome.color}30`,
                          borderRadius: 3, padding: '2px 6px', fontFamily: 'monospace',
                        }}>
                          NEAREST
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: '#7a6f5d', marginTop: 2 }}>
                      {biome.sequences.join(' · ')}
                    </div>
                  </div>

                  <div style={{ color: biome.color, fontSize: 12, opacity: 0.5 }}>
                    {isSelected ? '−' : '+'}
                  </div>
                </div>

                {/* Expanded hikmah note */}
                {isSelected && (
                  <div style={{
                    marginTop: 12, paddingTop: 12,
                    borderTop: `1px solid ${biome.color}15`,
                    animation: 'muralExpand 0.2s ease',
                  }}>
                    <style>{`@keyframes muralExpand { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
                    <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.7)', lineHeight: 1.7, fontStyle: 'italic', marginBottom: 10 }}>
                      {biome.description}
                    </p>
                    <div style={{
                      background: `${biome.color}08`,
                      border: `1px solid ${biome.color}15`,
                      borderRadius: 6, padding: '10px 12px',
                    }}>
                      <div style={{ fontSize: 8, color: biome.color, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 6, fontFamily: 'monospace' }}>
                        حِكْمَة — HIKMAH
                      </div>
                      <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.6, fontStyle: 'italic' }}>
                        {biome.hikmahNote}
                      </p>
                    </div>
                    <Link
                      href="/inner-reality-lens"
                      onClick={e => e.stopPropagation()}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        marginTop: 10, fontSize: 9, color: biome.color, textDecoration: 'none',
                        border: `1px solid ${biome.color}30`, borderRadius: 4, padding: '5px 10px',
                        background: `${biome.color}08`, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase',
                      }}
                    >
                      → Explore in IRL Lens
                    </Link>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Footer note */}
      <div style={{
        padding: '12px 20px',
        borderTop: '1px solid rgba(201,168,76,0.08)',
      }}>
        <p style={{ fontSize: 9, color: 'rgba(212,197,169,0.3)', fontStyle: 'italic', fontFamily: 'monospace' }}>
          The rest of the spiral is present but not foregrounded — until the nearest is recognized.
        </p>
      </div>
    </div>
  );
}

// ─── HIKMAH STATION (FRAMEWORK MAP) ──────────────────────────────────────────
// Singular wisdom-gathering clearing — you reach it by following the nearest path

export function HikmahStationFrameworkMap() {
  const [open, setOpen] = useState(false);
  const [activeLayer, setActiveLayer] = useState<'connections' | 'flow' | 'verses'>('connections');

  const connections = [
    { from: 'IRL Lens', to: 'Names Reference', label: 'Recognition → Names extracted', color: '#2d8a87' },
    { from: 'Keys', to: 'IRL Lens', label: 'Curriculum → Lens calibration', color: '#D4A82A' },
    { from: 'IRL Lens', to: 'MRG', label: 'Recognition → Amanah bridge', color: '#2d7a47' },
    { from: 'MRG', to: 'Community', label: 'Service → Collective sharing', color: '#5a8a5a' },
    { from: 'Open Letter', to: 'All Modules', label: 'Ground state → Everything grows from here', color: '#c9a84c' },
  ];

  const verses = [
    { ref: '2:164', text: 'Surely Signs for a people who use their intellect.', name: 'Al-Aql', color: '#2d8a87' },
    { ref: '27:60', text: 'Gardens of delightful beauty — not a tree of theirs was it for you to cause to grow.', name: 'Al-Khaliq', color: '#2d7a47' },
    { ref: '15:19-23', text: 'We have made for you in it means of living... We don\'t send down but in a known measure.', name: 'Ar-Razzaq', color: '#D4A82A' },
  ];

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'rgba(201,168,76,0.06)',
          border: '1px solid rgba(201,168,76,0.2)',
          borderRadius: 8, padding: '10px 16px',
          cursor: 'pointer', width: '100%', textAlign: 'left',
          transition: 'background 0.2s',
        }}
      >
        <span style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: '#c9a84c', direction: 'rtl' }}>حِكْمَة</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: 2 }}>
            HIKMAH STATION
          </div>
          <div style={{ fontSize: 11, color: '#7a6f5d' }}>
            A clearing where gathered information helps you see what flows through where you're standing.
          </div>
        </div>
        <span style={{ color: '#c9a84c', fontSize: 14 }}>→</span>
      </button>

      {/* Full panel */}
      {open && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 800,
            background: 'rgba(6,13,18,0.92)', backdropFilter: 'blur(12px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px',
          }}
          onClick={() => setOpen(false)}
        >
          <div
            style={{
              background: '#0a1218',
              border: '1px solid rgba(201,168,76,0.2)',
              borderRadius: 12,
              maxWidth: 600, width: '100%',
              maxHeight: '80vh', overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
              animation: 'hikmahOpen 0.3s ease',
            }}
            onClick={e => e.stopPropagation()}
          >
            <style>{`@keyframes hikmahOpen { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }`}</style>

            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid rgba(201,168,76,0.1)',
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: '#c9a84c', direction: 'rtl', marginBottom: 4 }}>
                  حِكْمَة — Hikmah Station
                </div>
                <p style={{ fontSize: 11, color: '#7a6f5d', lineHeight: 1.5, maxWidth: 380 }}>
                  "Gathered information highlighting connections, processes, and diagrams unfolding how resources flow throughout habitats and shared spaces — gesturing at purpose."
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                style={{ background: 'none', border: 'none', color: 'rgba(201,168,76,0.4)', cursor: 'pointer', fontSize: 20, padding: 4 }}
              >×</button>
            </div>

            {/* Layer tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid rgba(201,168,76,0.1)' }}>
              {(['connections', 'flow', 'verses'] as const).map(layer => (
                <button
                  key={layer}
                  onClick={() => setActiveLayer(layer)}
                  style={{
                    flex: 1, padding: '10px 12px',
                    background: activeLayer === layer ? 'rgba(201,168,76,0.06)' : 'transparent',
                    border: 'none',
                    borderBottom: activeLayer === layer ? '2px solid rgba(201,168,76,0.5)' : '2px solid transparent',
                    cursor: 'pointer',
                    fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                    color: activeLayer === layer ? '#c9a84c' : 'rgba(212,197,169,0.35)',
                    fontFamily: 'monospace',
                  }}
                >
                  {layer === 'connections' ? '◈ Connections' : layer === 'flow' ? '⌇ Flow' : '☽ Verses'}
                </button>
              ))}
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {activeLayer === 'connections' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <p style={{ fontSize: 11, color: '#7a6f5d', lineHeight: 1.6, marginBottom: 8, fontStyle: 'italic' }}>
                    How the modules connect — what flows from where to where.
                  </p>
                  {connections.map((conn, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: `${conn.color}06`,
                      border: `1px solid ${conn.color}20`,
                      borderRadius: 6, padding: '10px 14px',
                    }}>
                      <span style={{ fontSize: 11, color: '#d4c5a9', fontWeight: 600, minWidth: 80 }}>{conn.from}</span>
                      <div style={{ flex: 1, height: 1, background: `${conn.color}40`, position: 'relative' }}>
                        <div style={{
                          position: 'absolute', top: '50%', left: '50%',
                          transform: 'translate(-50%, -50%)',
                          background: '#0a1218', padding: '0 6px',
                          fontSize: 9, color: conn.color, fontFamily: 'monospace',
                          whiteSpace: 'nowrap',
                        }}>
                          →
                        </div>
                      </div>
                      <span style={{ fontSize: 11, color: '#d4c5a9', fontWeight: 600, minWidth: 80, textAlign: 'right' }}>{conn.to}</span>
                    </div>
                  ))}
                </div>
              )}

              {activeLayer === 'flow' && (
                <div>
                  <p style={{ fontSize: 11, color: '#7a6f5d', lineHeight: 1.6, marginBottom: 16, fontStyle: 'italic' }}>
                    Resources flow throughout habitats and shared spaces. The Hikmah Station is where you see the flow.
                  </p>
                  {/* Flow diagram */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {[
                      { label: 'Quran', desc: 'The source. Everything flows from here.', color: '#c9a84c', level: 0 },
                      { label: 'Open Letter', desc: 'The founder\'s recognition session. The first field notes.', color: '#c9a84c', level: 1 },
                      { label: 'Keys to Kulli Haal', desc: 'The curriculum. Soil nutrients for the garden.', color: '#D4A82A', level: 2 },
                      { label: 'IRL Lens', desc: 'The tool. Recognition in practice.', color: '#2d8a87', level: 2 },
                      { label: 'Names Reference', desc: 'The harvest. Names extracted from encounters.', color: '#8a8ab8', level: 3 },
                      { label: 'Muslim Resource Guide', desc: 'The Amanah. Recognition becomes service.', color: '#2d7a47', level: 3 },
                      { label: 'Community', desc: 'The call. Submit first, then share.', color: '#5a8a5a', level: 4 },
                    ].map((item, i) => (
                      <div key={i} style={{
                        marginLeft: item.level * 20,
                        background: `${item.color}08`,
                        border: `1px solid ${item.color}20`,
                        borderLeft: `2px solid ${item.color}50`,
                        borderRadius: '0 6px 6px 0',
                        padding: '8px 12px',
                      }}>
                        <div style={{ fontSize: 11, color: '#d4c5a9', fontWeight: 600, marginBottom: 2 }}>{item.label}</div>
                        <div style={{ fontSize: 10, color: '#7a6f5d' }}>{item.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeLayer === 'verses' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <p style={{ fontSize: 11, color: '#7a6f5d', lineHeight: 1.6, marginBottom: 4, fontStyle: 'italic' }}>
                    The Quranic verses that anchor the Mural. Each one a sign in the horizons.
                  </p>
                  {verses.map((verse, i) => (
                    <div key={i} style={{
                      background: `${verse.color}06`,
                      border: `1px solid ${verse.color}20`,
                      borderLeft: `3px solid ${verse.color}50`,
                      borderRadius: '0 8px 8px 0',
                      padding: '14px 16px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 9, color: verse.color, letterSpacing: '.12em', textTransform: 'uppercase', fontFamily: 'monospace' }}>
                          Quran {verse.ref}
                        </span>
                        <span style={{ fontSize: 9, color: verse.color, background: `${verse.color}15`, border: `1px solid ${verse.color}25`, borderRadius: 3, padding: '2px 6px', fontFamily: 'monospace' }}>
                          {verse.name}
                        </span>
                      </div>
                      <p style={{ fontSize: 12, color: '#d4c5a9', fontStyle: 'italic', lineHeight: 1.7, fontFamily: "'Georgia', serif" }}>
                        "{verse.text}"
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── VERNACULAR LOADING MESSAGES ──────────────────────────────────────────────

export const VERNACULAR_LOADING_MESSAGES = [
  'Form follows function on a journey to walk on some water.',
  'Life starts at the checkout, we still in the aisles.',
  'The curriculum begins with the sense(s) and builds toward environmental responsibility.',
  'Soil and nutrients, foundational elements that support growth.',
  'I didn\'t immediately notice when I transitioned from that snow covered sand onto the water.',
  'These bodies are the garments we wear, the seeds we plant.',
  'What\'s wrong, nothing is if you ain\'t, right?',
  'A structure formed reflecting on sample examinations, commentary, and field notes.',
];

export const VERNACULAR_GREETINGS: Record<string, string> = {
  morning: 'Life starts at the checkout, we still in the aisles. What are you putting in your cart today?',
  fajr: 'The sand nearest the frozen tides. The boundary is thinner than it looks right now.',
  dhuhr: 'Form follows function. What\'s the function today?',
  asr: 'The curriculum builds toward environmental responsibility. What responsibility is calling you?',
  maghrib: 'These bodies are the garments we wear, the seeds we plant. What did you plant today?',
  isha: 'Soil and nutrients, foundational elements that support growth. What grew today?',
  return: 'You were gone a minute. The garden kept growing. That\'s how it works.',
  first: 'I\'d like to walk with you on a journey processing what I see, a bit of how I see it, and what I recognize on the way.',
};

export function getVernacularGreeting(prayerState: string, isReturn: boolean, isFirst: boolean): string {
  if (isFirst) return VERNACULAR_GREETINGS.first;
  if (isReturn) return VERNACULAR_GREETINGS.return;
  return VERNACULAR_GREETINGS[prayerState] || VERNACULAR_GREETINGS.morning;
}
